import { Sidebar } from 'components/common';
import UserShortCut from 'components/common/UserShortCut';
import {
  detailResponseData,
  downloadFile,
  exportFormResponse,
  getResponseDetails,
} from 'features/forms/services/formServices';
import { RolesClient } from 'features/forms/types';
import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { hasPermission } from 'utils/permissions';
import { useAuthContext } from 'contexts/AuthContext';
import { FieldType as FieldFormType } from '../../types';
import { parseParametresOptions } from 'features/forms/utils/parametres';
import '../../../../styles/components/progress.css';
import {
  MintHrDropdown,
  MintHrDropdownItem,
} from 'components/common/MintHrDropdown';
import { changeResponseStatus } from 'features/forms/services/statusServices';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import { toast } from 'react-toastify';
import { useMediaContext } from 'contexts/MediaContext';
import {
  downloadFromArrayBuffer,
  validatePdfOrImg,
} from 'utils/globalMethodes';
import useFetchData from 'hooks/useFetch';
import { generateResponseHTML } from './DetailResponseTemplate';
import TruncateText from 'components/common/TruncateText';
import useClickOutside from 'hooks/useClickOutside';

const DetailResponse: FC<
  SideBarProps<{
    formId?: string;
    responseId: number;
    statusId?: number;
    refetch?: () => void;
  }>
> = ({ sidebar, setSidebarWidth, sidebarWidth, params }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { openMedia } = useMediaContext();
  const { closeSidebar } = useSidebarManager();
  const [show, setShow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, setShow, show);

  const { data: responseDetails, refetch } = useFetchData(getResponseDetails, [
    Number(params.responseId),
  ]);

  const changeStatus = async (statusInfo: { id: number; statutId: number }) => {
    const response = await changeResponseStatus(statusInfo);
    if (response.success) {
      toast.success(t('Default.succesMessage'));
      refetch();
      params.refetch?.();
    } else {
      toast.error(t(response.message));
    }
  };

  const handelOpenFile = async (fileName: string, title: string) => {
    const ext = fileName.split('.').pop() as string;
    openMedia(`/upload/VueFileByName/${fileName}`, ext, title);
  };
  const handleDownloadFile = async (fileName: string) => {
    try {
      const arrayBuffer = await downloadFile(fileName);
      downloadFromArrayBuffer(arrayBuffer, fileName);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };
  const handleDownloadResponse = async (
    responseId: number,
    FormName: string,
  ) => {
    try {
      const response = await detailResponseData(responseId);
      if (response) {
        const htmlContent = generateResponseHTML(response, {
          t: (key: string) => {
            const translated = t(key);
            return translated;
          },
          formatDate: (date: string) =>
            moment(date).format('DD MMM YYYY HH:mm'),
        });

        const arrayBuffer = await exportFormResponse(
          htmlContent,
          `Form_${FormName}-Response_${responseId}`,
        );
        downloadFromArrayBuffer(
          arrayBuffer,
          `Form_${FormName}-Response_${responseId}.pdf`,
        );
      } else {
        console.error('Error downloading response');
      }
    } catch (error) {
      console.error('Error downloading response:', error);
    }
  };

  if (!responseDetails?.data) {
    return null;
  }
  return (
    <Sidebar
      size="sm"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-mb-3">
        <h4 className="minthr-color-gray">{t('Form.Details')}</h4>

        <button
          className="minthr-sidebar-close"
          type="button"
          onClick={() => closeSidebar(DetailResponse)}
        >
          <img src="/icons/close.svg" alt="" />
        </button>
      </div>

      <div className="minthr-flex minthr-flex-items-center minthr-mb-5 minthr-pb-4 minthr-border-b minthr-border-b-3">
        <div className="minthr-flex minthr-flex-items-center">
          {responseDetails.data.user?.value ? (
            <UserShortCut
              userId={responseDetails.data.user?.value || ''}
              userPath={responseDetails.data.user?.imagePath || ''}
              alt={responseDetails.data.user?.text ?? ''}
              className="minthr-avatar minthr-avatar-m minthr-mr-2"
            />
          ) : (
            <img
              src="/icons/Group 65.svg"
              className="minthr-avatar minthr-avatar-m minthr-mr-2"
              alt=""
            />
          )}
          <div className="w-100">
            <div className="minthr-flex minthr-flex-items-center">
              <h4>
                {responseDetails.data.user != null
                  ? responseDetails.data.user.text
                  : t('Form.Anonymous.User')}
              </h4>
            </div>
            <p className="text-m minthr-mb-0 minthr-color-gray">
              {moment(responseDetails.data.createdDate).format(
                'DD MMM yyyy HH:mm',
              )}
            </p>
          </div>
        </div>
        <div className="minthr-flex minthr-flex-items-center minthr-ml-auto">
          {hasPermission('Form.ExportPdf') && (
            <button
              type="button"
              className="minthr-btn minthr-pl-1 minthr-btn-icon-origin exportpdfForms"
              // title="@SharedLocalizer.Get("Download.PDF")" data-title="@Model.FormName" data-url="@Url.Action("DownloadPdf", "Expenses", new { id = @Model.Id })"
              onClick={() =>
                handleDownloadResponse(
                  responseDetails.data.id,
                  responseDetails.data.formName,
                )
              }
            >
              <img
                className="minthr-icon-s"
                src="/icons/download.svg"
                alt="Download PDF"
              />
            </button>
          )}
          {responseDetails.data.statutList &&
            responseDetails.data.statutList.length > 0 &&
            (user.role === RolesClient.Proprietaire ||
              user.role === RolesClient.SupportMintHR ||
              hasPermission('Form.Administrate')) && (
              <div className="minthr-ml-auto" ref={dropdownRef}>
                <div className="minthr-select-badge-style-2">
                  {responseDetails.data.statutList.length > 1 ? (
                    <MintHrDropdown
                      title={
                        <>
                          <span
                            className="minthr-badge minthr-badge-m minthr-cursor-pointer"
                            style={{
                              backgroundColor:
                                responseDetails.data.statut.type || '',
                              color: 'black',
                              fontWeight: '400',
                            }}
                            onClick={() => setShow(!show)}
                          >
                            <small>
                              {' '}
                              {responseDetails.data.statut.text}
                              <img
                                className="minthr-icon-xs minthr-ml-2"
                                src="/icons/arrow-down.svg"
                                style={{
                                  transition: 'transform 0.3s ease',
                                  transform: show
                                    ? 'rotate(180deg)'
                                    : 'rotate(0)',
                                }}
                                alt=""
                              />
                            </small>
                          </span>
                        </>
                      }
                      className="minthr-select-badge Statut"
                    >
                      {responseDetails.data.statutList.map((status) => (
                        <MintHrDropdownItem
                          key={status.value}
                          style={{
                            backgroundColor: status.type || '',
                            fontWeight: 600,
                          }}
                          data-val={status.value}
                          onClick={() => {
                            changeStatus({
                              id: responseDetails.data.id,
                              statutId: Number(status.value),
                            });
                          }}
                        >
                          {t(status.text || '')}
                        </MintHrDropdownItem>
                      ))}
                    </MintHrDropdown>
                  ) : responseDetails.data.statut ? (
                    <span
                      data-val={responseDetails.data.statut.value}
                      className="minthr-ml-auto minthr-border-rounded-2-right minthr-pl-3 minthr-badge minthr-badge-s minthr-pr-3 minthr-text-truncate minthr-w-80"
                      style={{
                        backgroundColor: responseDetails.data.statut.type || '',
                        color: 'black',
                        fontWeight: 600,
                        cursor: 'default',
                      }}
                    >
                      {t(responseDetails.data.statut.text || '')}
                    </span>
                  ) : (
                    <span
                      data-val={responseDetails.data.statutList[0]?.value}
                      className="minthr-ml-auto minthr-border-rounded-2-right minthr-pl-3 minthr-badge minthr-badge-s minthr-pr-3 minthr-text-truncate minthr-w-80"
                      style={{
                        backgroundColor:
                          responseDetails.data.statutList[0]?.type || '',
                        color: 'black',
                        fontWeight: 600,
                        cursor: 'default',
                      }}
                    >
                      {t(responseDetails.data.statutList[0]?.text || '')}
                    </span>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>

      <div className="minthr-mb-4 minthr-pb-4 minthr-border-b">
        {responseDetails.data.statut &&
          responseDetails.data.statut.value != '' && (
            <div className="minthr-flex minthr-mb-1">
              <div className="minthr-w-45 minthr-flex">
                <span className="p text-m minthr-pr-2 minthr-color-gray">
                  {t('status')}
                </span>
                <span className="minthr-color-gray minthr-ml-auto h4 text-m">
                  :
                </span>
              </div>
              <div className="minthr-w-55 minthr-pl-4">
                <svg
                  width="10"
                  height="8"
                  viewBox="0 0 7 7"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="3.5"
                    cy="3.5"
                    r="3"
                    fill={responseDetails.data.statut.type || ''}
                  />
                </svg>
                <span
                  className="text-m minthr-font-weight-500 minthr-p-0 minthr-bg-transparent"
                  style={{ color: `${responseDetails.data.statut.type}` }}
                >
                  {' '}
                  {responseDetails.data.statut.text}
                </span>
              </div>
            </div>
          )}

        {/* @foreach (var item in Model.Fields) */}
        {responseDetails.data.fields &&
          responseDetails.data.fields.map((item, idx) => {
            // Only render if not description field
            if (item.fieldType != FieldFormType.Description) {
              return (
                <div key={idx} className="minthr-flex minthr-mb-1">
                  {item.fieldType != FieldFormType.InputFile && (
                    <div className="minthr-w-45 minthr-flex minthr-mr-1">
                      <span className="p text-m minthr-pr-2 minthr-color-gray">
                        {item.title}
                      </span>
                      <span className="minthr-color-gray minthr-ml-auto h4 text-m">
                        :
                      </span>
                    </div>
                  )}

                  {/* File handling */}
                  {(item.fieldType == FieldFormType.File ||
                    item.fieldType == FieldFormType.InputFile) &&
                  item.filesNames &&
                  item.filesNames.length > 0 ? (
                    <div
                      className={`${item.fieldType == FieldFormType.InputFile ? 'minthr-w-100' : 'minthr-w-50 minthr-ml-4'}`}
                    >
                      {item.filesNames.map((field) => {
                        const filename = field.path.split('.');
                        const ext = filename[filename.length - 1].toLowerCase();
                        let downloadClassName = 'DownLoadFile';
                        let srcImage = '/icons/download.svg';

                        const imageExtensions = ['jpg', 'jpeg', 'png'];

                        if (ext === 'pdf') {
                          downloadClassName = 'pdf-vue';
                          srcImage = '/icons/eye.svg';
                        } else if (imageExtensions.includes(ext)) {
                          downloadClassName = 'img-vue';
                          srcImage = '/icons/eye.svg';
                        }

                        const getFileIcon = () => {
                          switch (ext) {
                            case 'jpg':
                            case 'png':
                            case 'gif':
                            case 'jpeg':
                              return '/icons/doc-img.svg';
                            case 'pdf':
                              return '/icons/doc-pdf.svg';
                            case 'doc':
                            case 'docx':
                              return '/icons/doc-word.svg';
                            case 'xls':
                            case 'xlsx':
                              return '/icons/doc-excel.svg';
                            default:
                              return '/icons/doc-file.svg';
                          }
                        };

                        return (
                          <a
                            key={field.path}
                            className="minthr-flex minthr-flex-items-center  minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-3"
                          >
                            <img
                              className="minthr-icon-xl minthr-mr-2"
                              src={getFileIcon()}
                              alt=""
                            />
                            <div className=" minthr-pr-3 minthr-w-60">
                              <TruncateText
                                tag="h6"
                                maxCharacters={
                                  item.fieldType == FieldFormType.InputFile
                                    ? 40
                                    : 15
                                }
                                showTooltip
                              >
                                {field.name}
                              </TruncateText>
                            </div>
                            <button
                              className={`minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2 ${downloadClassName}`}
                              onClick={() => {
                                if (validatePdfOrImg(field.path)) {
                                  handelOpenFile(field.path, field.name);
                                } else {
                                  handleDownloadFile(field.path);
                                }
                              }}
                            >
                              <img
                                className="minthr-icon-s minthr-icon-gray"
                                src={srcImage}
                                alt=""
                              />
                            </button>
                          </a>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="minthr-w-55 minthr-pl-4">
                      {item.fieldType == FieldFormType.DateRange ||
                      item.fieldType == FieldFormType.TimeRange ? (
                        <>
                          {item.response &&
                            (() => {
                              const parts = item.response?.split(',');

                              if (parts && parts.length > 0 && parts[0]) {
                                if (parts.length > 1 && parts[1]) {
                                  return (
                                    <div className="text-m minthr-font-weight-500">
                                      {parts[0]} - {parts[1]}
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div className="text-m minthr-font-weight-500">
                                      {parts[0]}
                                    </div>
                                  );
                                }
                              }
                              return null;
                            })()}

                          {(item.fieldType === FieldFormType.DateRange ||
                            item.fieldType === FieldFormType.TimeRange) &&
                            (() => {
                              let durationDays: number | null = null;
                              let durationHours: number | null = null;
                              let durationMinutes: number | null = null;

                              if (item.response) {
                                const parts = item.response.split(',');

                                // Check for date range calculation
                                if (
                                  item.fieldType === FieldFormType.DateRange &&
                                  parts.length > 1
                                ) {
                                  try {
                                    const startDate = moment(
                                      parts[0],
                                      'DD/MM/YYYY',
                                    );
                                    const endDate = moment(
                                      parts[1],
                                      'DD/MM/YYYY',
                                    );

                                    if (
                                      startDate.isValid() &&
                                      endDate.isValid()
                                    ) {
                                      durationDays = endDate.diff(
                                        startDate,
                                        'days',
                                      );
                                    }
                                  } catch (error) {
                                    console.error(
                                      'Error parsing dates:',
                                      error,
                                    );
                                  }
                                }

                                // Check for time range calculation
                                if (
                                  item.fieldType === FieldFormType.TimeRange &&
                                  parts.length > 1
                                ) {
                                  try {
                                    const startTime = moment(parts[0], 'HH:mm');
                                    const endTime = moment(parts[1], 'HH:mm');

                                    if (
                                      startTime.isValid() &&
                                      endTime.isValid()
                                    ) {
                                      // If end time is before start time, add a day to end time
                                      if (endTime.isBefore(startTime)) {
                                        endTime.add(1, 'days');
                                      }

                                      const duration = moment.duration(
                                        endTime.diff(startTime),
                                      );
                                      durationHours = Math.floor(
                                        duration.asHours(),
                                      );
                                      durationMinutes = duration.minutes();
                                    }
                                  } catch (error) {
                                    console.error(
                                      'Error parsing times:',
                                      error,
                                    );
                                  }
                                }
                              }

                              return (
                                <>
                                  {durationDays !== null && (
                                    <div className="text-m minthr-font-weight-500">
                                      ({durationDays + 1} {t('solde.days')})
                                    </div>
                                  )}

                                  {(durationHours !== null ||
                                    durationMinutes !== null) && (
                                    <div className="text-m minthr-font-weight-500">
                                      ({durationHours || 0}{' '}
                                      {t('Reporting.hours')}{' '}
                                      {durationMinutes || 0}{' '}
                                      {t('Reporting.minutes')})
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                        </>
                      ) : (
                        <TruncateText
                          tag="div"
                          className="text-m minthr-font-weight-500"
                          maxCharacters={60}
                          showTooltip
                        >
                          {item.fieldType === FieldFormType.Checkbox &&
                          item.response
                            ? parseParametresOptions(item.response).join(', ')
                            : item.response}
                        </TruncateText>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
      </div>
      {responseDetails.data.activedApproval && (
        <div className="minthr-timeline">
          <div className="minthr-timeline-item minthr-pb-2">
            <div className="minthr-timeline-date-icon minthr-icon-bg minthr-icon-bg-xs minthr-icon-circle minthr-bg-primary-opacity">
              <img
                className="minthr-icon-succces minthr-icon-xs"
                src="/icons/plus.svg"
                alt=""
              />
            </div>
            <div className="minthr-timeline-content minthr-p-2 minthr-border-rounded minthr-border minthr-border-secondary">
              <div className="minthr-flex minthr-flex-space-between">
                {/* <img className="minthr-avatar minthr-avatar-s" src="@Url.Action("getprofileimage","Files", new{FileName=Model.CreateBy?.Image ?? null , userid=Model.CreateBy?.Id ?? null })" alt=""> */}
                <UserShortCut
                  userId={responseDetails.data.createBy.id || ''}
                  userPath={responseDetails.data.createBy.image || ''}
                  alt={responseDetails.data.createBy.fullName}
                  className="minthr-avatar minthr-avatar-s"
                />
                <div className="minthr-ml-2 minthr-w-40 minthr-mr-auto">
                  <div className="h5 minthr-text-truncate minthr-mb-1 text-m">
                    {responseDetails.data.createBy?.fullName}
                  </div>
                  <p className="minthr-mb-0 minthr-text-truncate text-m minthr-color-gray">
                    {t(responseDetails.data.createBy?.fonctionName)}
                  </p>
                </div>
                <div className="minthr-w-40 minthr-text-align-right">
                  <span className="minthr-icon-text">
                    <img
                      className="minthr-icon-xs minthr-ml-auto"
                      src="/icons/calendar.svg"
                      alt=""
                    />
                    {moment(responseDetails.data.createBy?.date).format(
                      'DD MMM yyyy HH:mm',
                    )}
                  </span>
                  <div className="minthr-flex minthr-flex-items-center">
                    <div className="progress progress-bar-s w-100">
                      <div
                        className="progress-bar minthr-progress-bg-primary"
                        role="progressbar"
                        style={{
                          width: `${responseDetails.data.createBy?.approvelTaux}%`,
                        }}
                        aria-valuenow={
                          responseDetails.data.createBy?.approvelTaux
                        }
                        aria-valuemin={0}
                        aria-valuemax={100}
                      ></div>
                    </div>
                    <span className="minthr-ml-2 text-s minthr-color-gray">
                      {' '}
                      {responseDetails.data.createBy?.approvelDone}/
                      {responseDetails.data.createBy?.approvelCount}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {responseDetails.data.approval &&
            responseDetails.data.approval.map((item, index) => {
              // Determine status-based styling
              const isAccept = item.statut.text === 'statutForm.accept';
              const isOnHold = item.statut.text === 'statutForm.onHold';

              const iconBgClass = isAccept
                ? 'minthr-bg-success'
                : isOnHold
                  ? 'minthr-bg-warning'
                  : 'minthr-bg-danger';

              const iconSrc = isAccept
                ? '/icons/check.svg'
                : isOnHold
                  ? '/icons/waiting.svg'
                  : '/icons/close.svg';

              return (
                <div key={index} className="minthr-timeline-item minthr-pb-2">
                  <div
                    className={`minthr-timeline-date-icon minthr-icon-bg minthr-icon-bg-xs minthr-icon-circle ${iconBgClass}`}
                  >
                    <img
                      className="minthr-icon-xxs minthr-icon-white minthr-bg-white"
                      src={iconSrc}
                      alt=""
                      width={16}
                      height={16}
                    />
                  </div>
                  <div className="minthr-timeline-content minthr-p-2 minthr-bg-light-gray minthr-border-rounded">
                    <div className="minthr-flex minthr-flex-space-between">
                      <UserShortCut
                        userId={item?.id || ''}
                        userPath={item?.image || ''}
                        alt={item?.level}
                        className="minthr-avatar minthr-avatar-s"
                      />
                      <div
                        className="minthr-ml-2 minthr-w-40 minthr-mr-auto"
                        style={{ width: '50%' }}
                      >
                        <div className="h5 minthr-text-truncate minthr-mb-1 text-m">
                          {item.fullName}
                        </div>
                        <p className="minthr-mb-0 minthr-text-truncate text-m minthr-color-gray">
                          {t(item.fonctionName)}
                        </p>
                      </div>
                      <div className="minthr-w-40 minthr-text-align-right">
                        {item.date && (
                          <span className="minthr-icon-text">
                            <img
                              className="minthr-icon-xs minthr-ml-auto"
                              src="/icons/calendar.svg"
                              alt=""
                            />
                            {moment(item.date).format('DD MMM YYYY HH:mm')}
                          </span>
                        )}
                        <div className="minthr-flex minthr-flex-items-center">
                          <span
                            className="text-m minthr-ml-auto minthr-p-0 minthr-hover-none"
                            style={{ color: item.statut.type || '' }}
                          >
                            <svg
                              width="10"
                              height="8"
                              viewBox="0 0 7 7"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="3.5"
                                cy="3.5"
                                r="3"
                                fill={item.statut.type || ''}
                              />
                            </svg>{' '}
                            {t(item.statut.text || '')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* @if (Model.StatutList != null && Model.StatutList.Count > 0 &&
(!this.User.UserRoles().Contains(ConstantsHelper.RolesClient.Proprietaire.ToString()) &&
!this.User.UserRoles().Contains(ConstantsHelper.RolesClient.SupportMintHR.ToString()) &&
!(await PermissionCheck.HasPermission("Form.Administrate")))) */}
      {responseDetails.data.statutList &&
        responseDetails.data.statutList.length > 0 &&
        user.role !== RolesClient.Proprietaire &&
        user.role !== RolesClient.SupportMintHR &&
        !hasPermission('Form.Administrate') && (
          <div className="minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-py-4 minthr-mx-n5 minthr-px-4 minthr-mt-auto">
            {(() => {
              const refuseItem = responseDetails.data.statutList.find(
                (x) => x.text === 'statutForm.refuse',
              );
              return (
                <>
                  {refuseItem && (
                    <button
                      type="button"
                      className="minthr-btn minthr-btn-outline minthr-px-6 Statut"
                      data-val={refuseItem.value}
                      style={{
                        border: `1px solid ${refuseItem.type}`,
                        color: refuseItem.type || '',
                      }}
                      onClick={() =>
                        changeStatus({
                          id: responseDetails.data.id,
                          statutId: Number(refuseItem.value),
                        })
                      }
                    >
                      {t(refuseItem.text || '')}
                    </button>
                  )}

                  {/* Filter and map other status buttons */}
                  {responseDetails.data.statutList
                    .filter(
                      (item) =>
                        item.text !== 'statutForm.onHold' &&
                        item.text !== 'statutForm.refuse',
                    )
                    .map((item, index) => (
                      <button
                        key={index}
                        type="button"
                        className="minthr-btn minthr-btn-outline minthr-px-6 Statut"
                        data-val={item.value}
                        style={{
                          border: `1px solid ${item.type}`,
                          color: item.type || '',
                        }}
                        onClick={() =>
                          changeStatus({
                            id: responseDetails.data.id,
                            statutId: Number(item.value),
                          })
                        }
                      >
                        {t(item.text || '')}
                      </button>
                    ))}
                </>
              );
            })()}
          </div>
        )}
    </Sidebar>
  );
};

export default DetailResponse;
